import {v4} from "uuid";
import {pickBy} from "lodash";
import {blockIsVisible, computeChildrenBlock} from "@reside/forms";

import {categorizeSlides} from "./helpers/slide";
import {AdmissionStatus} from "../services/AdmissionsService";
import {calculatePrintName} from "./AdmissionModel.helpers";

export function computeProps(instance, answers, overrides) {
  return {
    readOnly:
      [AdmissionStatus.IN_PROGRESS, AdmissionStatus.PRE_FLIGHT].includes(
        instance.status,
      ) === false,
    printName: calculatePrintName(answers),
    ...overrides,
  };
}

export function calculateCompletionPercentage(slides) {
  let completionPercentage = 0;

  if (slides) {
    const totalSlides = slides.filter(
      slide => slide.state.visible && !slide.state.last,
    );
    const completeSlides = totalSlides.filter(slide => slide.state.complete);

    completionPercentage = Math.round(
      completeSlides.length / (totalSlides.length / 100),
    );
  }

  return completionPercentage;
}

export function extractAfterAdmissionSlides({template, answers}) {
  const afterAdmissionSlides = [];

  template.children[template.children.length - 1].children.forEach(section => {
    const sectionIsVisible = blockIsVisible(answers, section);
    section.children
      .filter(slide => slide.afterAdmission)
      .forEach(slide => {
        afterAdmissionSlides.push({
          ...slide,
          children: slide.children?.map(node =>
            computeChildrenBlock({
              node,
              answers,
              template,
            }),
          ),
          sectionId: section.id,
          sectionTitle: section.title,
          state: {
            disabled: false,
            complete: false,
            fullscreen:
              !!slide.backgroundUrl ||
              slide.children[0].type === "tableOfContent",
            last: false,
            visible: sectionIsVisible && blockIsVisible(answers, slide),
            flag: null,
          },
        });
      });
  });

  return afterAdmissionSlides;
}

export const getPreflightedAnswers = answers =>
  pickBy(answers, answer => answer.preFlight);

export function extractFlags(instance) {
  return instance.flags.reduce((obj, flag) => {
    obj[flag.id] = {
      id: flag.id,
      author: flag.author,
      comment: flag.comment,
      created: flag.created,
      slideId: flag.slideId,
    };

    return obj;
  }, {});
}

export function extractNotes(instance) {
  return instance.notes.reduce((obj, note) => {
    if (!obj[note.slideId]) {
      obj[note.slideId] = [];
    }
    obj[note.slideId].push(note);

    return obj;
  }, {});
}

export function buildToc(template, slides, incomplete) {
  const indexedSlides = slides.map((s, idx) => ({...s, idx: idx}));
  const categorized = categorizeSlides(indexedSlides);
  const filteredSlides = incomplete
    ? categorized.visibleIncomplete
    : categorized.visible;

  // Pre-index slides by sectionId and categorize each section in a single pass
  const slidesBySectionId = new Map();
  const categorizedBySectionId = new Map();
  let completedSlidesCount = 0;
  for (const slide of filteredSlides) {
    if (!slidesBySectionId.has(slide.sectionId)) {
      slidesBySectionId.set(slide.sectionId, []);
    }
    slidesBySectionId.get(slide.sectionId).push(slide);
    if (slide.state?.complete) completedSlidesCount++;
  }
  for (const [sectionId, sectionSlides] of slidesBySectionId) {
    categorizedBySectionId.set(sectionId, categorizeSlides(sectionSlides));
  }

  const state = {
    totalSlides: filteredSlides.length,
    completedSlides: completedSlidesCount,
  };

  const groups = template.children.map(group => {
    const groupState = group.children.reduce(
      (groupCnt, section) => {
        const sectionCategorized = categorizedBySectionId.get(section.id);
        const sectionTotal = slidesBySectionId.get(section.id)?.length || 0;

        return {
          totalSlides: groupCnt.totalSlides + sectionTotal,
          completedSlides:
            groupCnt.completedSlides +
            (sectionCategorized?.complete.length || 0),
          flaggedSlides:
            groupCnt.flaggedSlides +
            (sectionCategorized?.flagged.length || 0),
        };
      },
      {totalSlides: 0, completedSlides: 0, flaggedSlides: 0},
    );

    return {
      id: group.id,
      title: group.title,
      state: groupState,
      children: group.children.map(section => {
        const slidesInSection = slidesBySectionId.get(section.id) || [];
        const uniqueNamedSlides = getUniqueNamedSlides(slidesInSection);

        return {
          id: section.id,
          title: section.title,
          children: uniqueNamedSlides,
        };
      }),
    };
  }, {});

  return {groups: groups, state: state};
}

const getUniqueNamedSlides = slides => {
  // Use Map for O(1) lookup instead of O(n) .find() inside reduce
  // Note: Only store first occurrence per translationKey to match original .find() behavior
  const slidesByTranslationKey = new Map();
  const result = [];

  for (let idx = 0; idx < slides.length; idx++) {
    const slide = slides[idx];
    const translationKey = slide.title.translationKey;
    const prevSlide = idx > 0 ? slides[idx - 1] : null;
    const isPrevSameTitle =
      prevSlide && prevSlide.title.translationKey === translationKey;

    if (isPrevSameTitle) {
      // Increment flag count on first unique slide with this title
      // (matches original arr.find() behavior which returns first match)
      const existingSlide = slidesByTranslationKey.get(translationKey);
      if (existingSlide && slide.state.flag !== null) {
        existingSlide.state.totalFlags += 1;
      }
    } else {
      // Create new unique slide entry
      const modifiedSlide = {
        ...slide,
        state: {
          ...slide.state,
          totalFlags: slide.state.flag !== null ? 1 : 0,
        },
      };
      // Only set in Map if this is the first occurrence of this translationKey
      // This preserves original .find() behavior that returns first match
      if (!slidesByTranslationKey.has(translationKey)) {
        slidesByTranslationKey.set(translationKey, modifiedSlide);
      }
      result.push(modifiedSlide);
    }
  }

  return result;
};

/**
 * Remap key-value object to structure suitable to be sent to server.
 */
export function buildCompletionChecklist(slides) {
  return slides.reduce((arr, slide) => {
    if (slide.state.visible && slide.inCompletionChecklist) {
      arr.push({
        id: slide.id,
        title: slide.title,
        complete: slide.state.complete,
      });
    }

    return arr;
  }, []);
}

/**
 * Create Flag
 */
export function createFlag(payload) {
  return {
    id: v4(),
    created: new Date().getTime(),
    author: payload.author,
    comment: payload.comment,
    slideId: payload.slideId,
  };
}
